<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import lottie from 'lottie-web'

interface Props {
  animationData?: any
  path?: string
  loop?: boolean
  autoplay?: boolean
  speed?: number
  width?: string | number
  height?: string | number
}

const props = withDefaults(defineProps<Props>(), {
  loop: true,
  autoplay: true,
  speed: 1,
  width: '100%',
  height: '100%',
})

const lottieContainer = ref<HTMLElement>()
let animationInstance: any = null

const initAnimation = () => {
  if (!lottieContainer.value)
    return

  const container = lottieContainer.value

  // Clear previous animation
  if (animationInstance)
    animationInstance.destroy()

  const animationConfig: any = {
    container,
    loop: props.loop,
    autoplay: props.autoplay,
    renderer: 'svg',
  }

  if (props.animationData)
    animationConfig.animationData = props.animationData
  else if (props.path)
    animationConfig.path = props.path

  animationInstance = lottie.loadAnimation(animationConfig)

  if (props.speed !== 1)
    animationInstance.setSpeed(props.speed)
}

const play = () => {
  if (animationInstance)
    animationInstance.play()
}

const pause = () => {
  if (animationInstance)
    animationInstance.pause()
}

const stop = () => {
  if (animationInstance)
    animationInstance.stop()
}

const goToAndPlay = (value: number) => {
  if (animationInstance)
    animationInstance.goToAndPlay(value)
}

const goToAndStop = (value: number) => {
  if (animationInstance)
    animationInstance.goToAndStop(value)
}

// Expose methods
defineExpose({
  play,
  pause,
  stop,
  goToAndPlay,
  goToAndStop,
})

onMounted(() => {
  initAnimation()
})

onUnmounted(() => {
  if (animationInstance)
    animationInstance.destroy()
})

// Watch for prop changes
watch(() => [props.animationData, props.path], () => {
  initAnimation()
}, { deep: true })
</script>

<template>
  <div ref="lottieContainer" class="lottie-container" />
</template>

<style scoped>
.lottie-container {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
